import type { QueryProviderProps } from 'src/modules/shared/types/query-provider.type';
import type { QueryClientConfig } from '@tanstack/react-query';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useSetApiToken } from 'hooks/api/api';
import React, { useState } from 'react';
import { useApiRetryPolicy } from 'src/modules/shared/hooks/api/use-api-retry-policy';
import { isCallerHandledQueryError } from 'src/modules/shared/utils/api/query-error-policy.util';
import { MINUTE_IN_MS } from 'src/utils/date';

/** Supplies the application query cache and shared retry/error policy. */
const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  const {
    shouldRetry: shouldRetryRequest,
    retryDelay,
    onTerminalError,
  } = useApiRetryPolicy();

  const onRetryLimit = (error: Error) => {
    void onTerminalError(error);
  };

  const queryClientConfig: QueryClientConfig = {
    defaultOptions: {
      queries: {
        staleTime: MINUTE_IN_MS,
        retryDelay,
        retry: shouldRetryRequest,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!isCallerHandledQueryError(query.meta)) onRetryLimit(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: onRetryLimit,
    }),
  };
  const [queryClient] = useState(() => new QueryClient(queryClientConfig));

  useSetApiToken();

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default QueryProvider;
